// Angular
import {Injectable} from '@angular/core';
import {Location} from '@angular/common';

// NGRX
import {select, Store} from '@ngrx/store';
import {take} from 'rxjs/operators';

// Miscellaneous
import {
  TrackPageErrorEventAction,
  TrackTelemetryErrorAction,
  TrackClearTelemetryErrorAction
} from 'src/app/actions/tracking';
import * as fromRoot from '../../../reducers';
import * as fromAnalytics from '../../../reducers/analytics.reducer';
import {TelemetryGenericEnum} from '../../../shared/enums';
import {analyticsConstants, constants} from '../../../constants';
import {TrackErrorPayload} from 'src/app/models/action-payloads/tracking-payload';
import {AnalyticsTelemetryErrorData, SplunkBackendErrorInterface,} from '../../../shared/interfaces';
import { SplunkService } from 'src/app/shared/services/splunk.service';

@Injectable({
  providedIn: 'root'
})
export class TealiumService {

  constructor(
    private store   : Store<fromRoot.AppState>,
    private location: Location,
    private splunkService: SplunkService,
  ) { }

  analyticsInfoNotAvailable() {
    //Specific event for Information not available screen
		this.store
    .pipe(
      select(fromRoot.getAnalyticsState),
      take(1)
    )
    .subscribe(data => {
      const telemetryData = this.retrieveAnalyticsInfoNotAvailableResponse(data);
      this.store.dispatch(new TrackPageErrorEventAction(telemetryData));
      this.dispatchSplunkErrorPageView(telemetryData);
      this.store.dispatch(new TrackClearTelemetryErrorAction());
    });
  }

  dispatchSplunkErrorPageView(errorPayload: TrackErrorPayload) {
    const context = this.splunkService.initializeContext(
      ['error', 'message'],
      [errorPayload, 'Information is currently not available']
    );
    const splunkError = this.splunkService.errorByLookUp(
      'GC-033-CHECKIN-CURRENTLY-NOT-AVAILABLE-ERROR-PAGE',
      { context }
    );
    this.dispatchAnalyticsBackendError(splunkError);
  }

  retrieveAnalyticsInfoNotAvailableResponse(data: any): TrackErrorPayload {
    return {
      currentPage: TelemetryGenericEnum.CHECK_IN_ERROR_PAGE,
      pageName: TelemetryGenericEnum.CHECK_IN_ERROR_PAGE,
      errorMsg: `${TelemetryGenericEnum.DEFAULT_FAILURE}: Information is currently not available. Please try again later.`,
      errorIdentifier: data.errorIdentifier,
      errorCode: data.errorCode,
      backendError: this.retrieveBackendErrorNotAvailable(data),
      referringLinkLocation: data.currentPage,
      interaction: TelemetryGenericEnum.INLINE_ERROR,
    } as TrackErrorPayload;
  }

  retrieveAnalyticsAlertInlineResponse(currentPage: string, interaction: string, alertMessage: string, isAlert = true, trackErrorPayload?: TrackErrorPayload) {
    const prefix = isAlert ? TelemetryGenericEnum.DEFAULT_ALERT : TelemetryGenericEnum.DEFAULT_FAILURE;
    const alertInlineResponse = {
      currentPage: isAlert ? currentPage : this.getCurrentPage(),
      pageName: isAlert ? currentPage : this.getCurrentPage(),
      channel: analyticsConstants.CHANNEL,
      interaction,
      eventType: TelemetryGenericEnum.INTERACTION_EVENT_TYPE,
      errorMsg: `${prefix}: ${alertMessage}`
    };
    return (
      trackErrorPayload
      ? {
        ...alertInlineResponse,
        errorIdentifier: trackErrorPayload?.errorIdentifier ?? TelemetryGenericEnum.EMPTY_VALUE,
        errorCode: trackErrorPayload.errorCode ?? TelemetryGenericEnum.EMPTY_VALUE,
        backendError: this.retrieveBackendErrorNotAvailable(trackErrorPayload),
        referringLinkLocation: trackErrorPayload.currentPage ?? TelemetryGenericEnum.EMPTY_VALUE
      }
      : { ...alertInlineResponse }
    ) as TrackErrorPayload;
  }

  getCurrentPage(): string {
    const path = this.location.path();
    let pageName;
    if (path.indexOf('/') === 0) {
      pageName = path.replace('/', '').split('?')[0];
    } else {
      pageName = path.split('?')[0];
    }
    if (pageName.indexOf('/') !== -1) {
      pageName = pageName.replace('/', ':');
    }
    if (pageName === constants.PAGE_NAME_FOR_TRACKING.BOARDING_PASS) {
      pageName = constants.PAGE_NAME_FOR_TRACKING.SSP;
    }
    return pageName;
  }

  dispatchAnalyticsBackendError(
    splunkError: any
  ) {
    const telemetryData: AnalyticsTelemetryErrorData = this.retrieveAnalyticsBackendError(splunkError);
    this.store.dispatch(new TrackTelemetryErrorAction(telemetryData));
  }

  retrieveStatusMessageNullableResponse() {
    return [{ errorCode: TelemetryGenericEnum.NO_VALUE_ERROR_MSG, statusMessage: TelemetryGenericEnum.UNKNOWN_BACKEND_ERROR }];
  }

  retrieveAnalyticsBackendError(
    splunkError: SplunkBackendErrorInterface
  ): AnalyticsTelemetryErrorData {
    return {
      currentPage: this.getCurrentPage(),
      interaction: TelemetryGenericEnum.INLINE_ERROR,
      errorMsg: this.retrieveErrorMessage(splunkError),
      errorIdentifier: splunkError?.eventId ?? TelemetryGenericEnum.EMPTY_VALUE,
      errorCode: splunkError?.errId,
      statusCode: this.retrieveStatusCode(splunkError),
      backendError: this.retrieveBackendError(splunkError),
      url: splunkError?.context?.error?.path ?? TelemetryGenericEnum.EMPTY_VALUE,
    }
  }

  retrieveAnalyticsBackendErrorInteraction(data: fromAnalytics.State): TrackErrorPayload {
    return {
      currentPage: data.currentPage,
      pageName: data.currentPage,
      errorMsg: data.errorMsg,
      errorIdentifier: data.errorIdentifier,
      errorCode: data.errorCode,
      backendError: data.backendError,
      referringLinkLocation: data.currentPage,
      interaction: data.interaction,
    } as TrackErrorPayload;
  }

  retrieveErrorMessage(
    splunkError: SplunkBackendErrorInterface
  ): string {
    if (!splunkError?.context) return TelemetryGenericEnum.EMPTY_VALUE;

    return 'Sorry, we hit a snag. We\'re unable to complete your request. Please try again later.';
  }

  retrieveStrToError(message: string) {
    return `${TelemetryGenericEnum.DEFAULT_FAILURE}: ${message}`;
  }

  retrieveBackendError(
    splunkError: SplunkBackendErrorInterface
  ): string {
    return `statusCode:${this.retrieveStatusCode(splunkError)}|endpoint:${splunkError.context?.error?.path ?? TelemetryGenericEnum.EMPTY_VALUE}`
  }

  retrieveBackendErrorNotAvailable(data: any) {
    return `statusCode:${data.statusCode || 'No value'}|endPoint:${data.url || ''}|telemetryErrorMessage:${data.errorMsg}|serviceErrorMessage:${data.backendError}`;
  }

  retrieveStatusCode(
    splunkError: SplunkBackendErrorInterface
  ) {
    const statusNumber = splunkError.context?.error?.statusCode ?? splunkError.context?.error?.status ?? TelemetryGenericEnum.DEFAULT_STATUS_CODE;
    return typeof statusNumber === 'string' ? parseInt(statusNumber) : statusNumber;
  }
}
